/**
 * Signing out.
 *
 * Two things have to happen and neither is optional: the refresh token is
 * revoked server-side so it cannot be replayed, and every cached response is
 * dropped so the next person to use this browser does not see the last one's
 * rides while their first request is in flight.
 *
 * The local half runs even when the network call fails. A person who pressed
 * sign out on a shared machine must not still be signed in because the request
 * timed out.
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { api, useLogoutMutation } from "../api";
import { sessionCleared } from "../store/slices/authSlice";
import { useAppDispatch, useAppSelector } from "../store/hooks";

export function useSignOut(): () => Promise<void> {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const refreshToken = useAppSelector((state) => state.auth.refreshToken);
    const [logout] = useLogoutMutation();

    return useCallback(async () => {
        if (refreshToken) {
            try {
                // The mutation's own onQueryStarted clears the session and the
                // cache in a finally block, so both happen either way.
                await logout({ refreshToken }).unwrap();
            } catch {
                // Revocation failed; the local session is cleared regardless.
            }
        } else {
            dispatch(sessionCleared());
            dispatch(api.util.resetApiState());
        }
        void navigate("/", { replace: true });
    }, [dispatch, logout, navigate, refreshToken]);
}

export default useSignOut;
